import {
  createBrowserRouter,
  Navigate,
  redirect,
  type LoaderFunctionArgs,
  type ShouldRevalidateFunctionArgs,
} from 'react-router-dom';
import { wrapCreateBrowserRouter } from '@sentry/react';
import { StaleClientError } from '@immo/api-contract';
import { AdminLayout } from '@/components/layout/admin-layout';
import { visibleItemsFor } from '@/components/layout/sidebar';
import { LoadingPage } from '@/pages/loading';
import { NotFoundPage } from '@/pages/not-found';
import { RouteErrorPage } from '@/pages/route-error';
import { SignInPage } from '@/pages/auth/sign-in';
import { AuthCallbackPage } from '@/pages/auth/callback';
import { SignOutPage } from '@/pages/auth/sign-out';
import { AccessDeniedPage } from '@/pages/access-denied';
import { WorkspacesPage, workspacesLoader, workspacesLoaderParams } from '@/pages/workspaces';
import { NewWorkspacePage } from '@/pages/workspaces/new';
import { WorkspaceDetailPage, workspaceDetailLoader } from '@/pages/workspaces/detail';
import { WorkspaceEditPage, workspaceEditLoader } from '@/pages/workspaces/edit';
import { WorkspaceEmailTemplateModal, workspaceEmailTemplateLoader } from '@/pages/workspaces/email-template-modal';
import { MembersPage, membersLoader, membersLoaderParams } from '@/pages/members';
import { MemberDetailPage, memberDetailLoader } from '@/pages/members/detail';
import { MemberAccessPage, memberAccessLoader } from '@/pages/members/access';
import { MemberEditPage, memberEditLoader } from '@/pages/members/edit';
import { OrganizationsPage, organizationsLoader, organizationsLoaderParams } from '@/pages/organizations';
import { OrganizationDetailPage, organizationDetailLoader } from '@/pages/organizations/detail';
import { OrganizationEditPage, organizationEditLoader } from '@/pages/organizations/edit';
import { OrganizationMovePage, organizationMoveLoader } from '@/pages/organizations/move';
import { PropertiesPage, propertiesLoader, propertiesLoaderParams } from '@/pages/properties';
import { PropertyDetailPage, propertyDetailLoader } from '@/pages/properties/detail';
import { ApiKeysPage, apiKeysLoader, apiKeysLoaderParams } from '@/pages/api-keys';
import { IssueApiKeyPage } from '@/pages/api-keys/new';
import { IssueAdminKeyPage } from '@/pages/api-keys/new-admin-key';
import { ApiKeyDetailPage, apiKeyDetailLoader } from '@/pages/api-keys/detail';
import { AdminApiKeysPage, adminApiKeysLoader } from '@/pages/admins/api-keys';
import { ApiKeyRequestsPage, apiKeyRequestsLoader, apiKeyRequestsLoaderParams } from '@/pages/api-key-requests';
import { ApiKeyRequestDetailPage, apiKeyRequestDetailLoader } from '@/pages/api-key-requests/detail';
import { ApiCallsPage, apiCallsLoader, apiCallsLoaderParams } from '@/pages/api-calls';
import { EmailLogPage, emailLogLoader, shouldRevalidateEmailLog } from '@/pages/email-log';
import { ApiCallDetailPage, apiCallDetailLoader } from '@/pages/api-calls/detail';
import { DeletedCompaniesPage, deletedCompaniesLoader, deletedCompaniesLoaderParams } from '@/pages/companies/deleted';
import { DeletedDealsPage, deletedDealsLoader, deletedDealsLoaderParams } from '@/pages/deals/deleted';
import {
  ValuationRequestsPage,
  valuationRequestsLoader,
  valuationRequestsLoaderParams,
} from '@/pages/valuation-requests';
import { ValuationRequestDetailPage, valuationRequestDetailLoader } from '@/pages/valuation-request-detail';
import { MessengerChatsPage, messengerChatsLoader, messengerChatsLoaderParams } from '@/pages/messenger-chats';
import { MessengerChatDetailPage, messengerChatDetailLoader } from '@/pages/messenger-chats/chat-detail';
import { AdminsPage, adminsLoader, adminsLoaderParams } from '@/pages/admins';
import {
  DocumentAnalysisSkillsPage,
  documentAnalysisSkillsLoader,
  documentAnalysisSkillsLoaderParams,
} from '@/pages/skills/document-analyses';
import {
  DocumentAnalysisSkillEditorPage,
  documentAnalysisSkillEditorLoader,
} from '@/pages/skills/document-analysis-editor';
import { SystemSkillEditorPage, systemSkillEditorLoader } from '@/pages/skills/system-skill-editor';
import { FactKeysPage, factKeysLoader, factKeysLoaderParams } from '@/pages/skills/fact-keys';
import { AgentSkillsPage, agentSkillsLoader, agentSkillsLoaderParams } from '@/pages/skills/agent-skills';
import { AgentSkillEditorPage, agentSkillEditorLoader } from '@/pages/skills/agent-skill-editor';
import { NewAgentSkillPage } from '@/pages/skills/agent-skill-new';
import { NewAdminPage } from '@/pages/admins/new';
import { AdminDetailPage, adminDetailLoader } from '@/pages/admins/detail';
import { McpAccessPage, mcpAccessLoader, mcpAccessLoaderParams } from '@/pages/mcp-access';
import { IssueMcpAccessPage } from '@/pages/mcp-access/new';
import { ValuationAgentPage, valuationAgentLoader } from '@/pages/valuation-agent';
import { revalidateOn } from '@/lib/list-view/revalidate-on';
import { FleetPage, fleetLoader } from '@/pages/fleet';
import { FleetBoardPage } from '@/pages/fleet/board';
import { FleetHistoryPage } from '@/pages/fleet/history';
import { FleetMetricsPage, fleetMetricsLoader } from '@/pages/fleet/metrics';
import { FleetAccountsPage, fleetAccountsLoader } from '@/pages/fleet/accounts';
import { LandIqDataRunsPage } from '@/pages/landiq-data-runs';
import { SandboxPage } from '@/pages/sandbox';
import { LandIqDataRunDetailPage } from '@/pages/landiq-data-run-detail';
import { fetchMe } from '@/lib/auth/session';

async function requireSession() {
  try {
    const me = await fetchMe();

    if (me === null) {
      throw redirect('/sign-in');
    }

    return me;
  } catch (err) {
    if (err instanceof Response) {
      throw err;
    }

    // A bundle too old to read the API's answer is not a broken session, and
    // this catch guards every protected page. Swallowed into the redirect it
    // would sign a signed-in operator out, with the reload offer unreachable on
    // the app's primary path.
    if (err instanceof StaleClientError) {
      throw err;
    }

    console.error('[requireSession] unable to verify admin session; routing to /sign-in', err);
    throw redirect('/sign-in');
  }
}

// `/logs` has no page of its own — it resolves to the first log this operator
// may read. Holding none is a permission refusal, not an empty page: the 403
// reaches `RouteErrorPage`, which routes it to the missing-permission message.
async function logsIndexLoader() {
  const me = await fetchMe();

  if (me === null) throw redirect('/sign-in');

  const granted = new Set<string>(me.user.permissions);
  const firstReadable = visibleItemsFor(granted).find((item) => item.path.startsWith('/logs'))?.children?.[0];

  if (firstReadable === undefined) throw new Response('No readable log', { status: 403 });

  throw redirect(firstReadable.path);
}

// Exported as a plain array so tests can drive the *real* route table through
// `createMemoryRouter(routes)` rather than a hand-built fixture — a fixture that
// omits `/workspaces/:id` cannot observe the IMM-513 defect (see
// legacy-log-addresses.test.tsx).
export const routes = [
  {
    path: '/sign-in',
    element: <SignInPage />,
    errorElement: <RouteErrorPage />,
  },
  {
    path: '/auth/callback',
    element: <AuthCallbackPage />,
    errorElement: <RouteErrorPage />,
  },
  {
    path: '/sign-out',
    element: <SignOutPage />,
    errorElement: <RouteErrorPage />,
  },
  {
    path: '/access-denied',
    element: <AccessDeniedPage />,
    errorElement: <RouteErrorPage />,
  },
  {
    id: 'admin-protected',
    element: <AdminLayout />,
    loader: requireSession,
    // A same-URL revalidation — the test-workspace pill re-running a list's
    // loader (IMM-593.2), a fetcher submit — must not re-check the session:
    // `requireSession` is a round-trip to /v1/admin/auth/me, and the session
    // is re-checked on every navigation anyway. Navigating elsewhere still
    // re-runs it.
    shouldRevalidate: ({ currentUrl, nextUrl }: ShouldRevalidateFunctionArgs) =>
      currentUrl.pathname !== nextUrl.pathname,
    hydrateFallbackElement: <LoadingPage />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        // <Navigate> instead of a loader-redirect: if requireSession
        // throws redirect('/sign-in'), this element never renders, so the
        // unauth path is a single navigation (not a redirect-to-workspaces
        // round-trip that re-runs every protected loader).
        index: true,
        element: <Navigate to="/workspaces" replace />,
      },
      {
        path: '/workspaces',
        element: <WorkspacesPage />,
        loader: workspacesLoader,
        shouldRevalidate: revalidateOn(workspacesLoaderParams),
        handle: { titleKey: 'pages.admin.workspaces.title' },
        children: [
          {
            path: 'new',
            element: <NewWorkspacePage />,
            handle: { titleKey: 'pages.admin.workspaces.create.title' },
          },
        ],
      },
      {
        path: '/workspaces/:id',
        element: <WorkspaceDetailPage />,
        loader: workspaceDetailLoader,
        handle: { titleKey: 'pages.admin.workspaces.detail.title' },
        children: [
          {
            path: 'edit',
            element: <WorkspaceEditPage />,
            loader: workspaceEditLoader,
            handle: { titleKey: 'pages.admin.workspaces.edit.title' },
          },
          {
            path: 'email-templates/:emailTemplateId',
            element: <WorkspaceEmailTemplateModal />,
            loader: workspaceEmailTemplateLoader,
            handle: { titleKey: 'pages.admin.workspaces.email-templates.title' },
          },
        ],
      },
      {
        path: '/workspaces/members',
        element: <MembersPage />,
        loader: membersLoader,
        shouldRevalidate: revalidateOn(membersLoaderParams),
        handle: { titleKey: 'pages.admin.members.title' },
      },
      {
        path: '/workspaces/members/:id',
        element: <MemberDetailPage />,
        loader: memberDetailLoader,
        handle: { titleKey: 'pages.admin.members.detail.title' },
        children: [
          {
            path: 'edit',
            element: <MemberEditPage />,
            loader: memberEditLoader,
            handle: { titleKey: 'pages.admin.members.edit.title' },
          },
          {
            path: 'access',
            element: <MemberAccessPage />,
            loader: memberAccessLoader,
            handle: { titleKey: 'pages.admin.members.access.title' },
          },
        ],
      },
      {
        path: '/workspaces/organizations',
        element: <OrganizationsPage />,
        loader: organizationsLoader,
        shouldRevalidate: revalidateOn(organizationsLoaderParams),
        handle: { titleKey: 'pages.admin.organizations.title' },
      },
      {
        path: '/workspaces/organizations/:id',
        element: <OrganizationDetailPage />,
        loader: organizationDetailLoader,
        handle: { titleKey: 'pages.admin.organizations.detail.title' },
        children: [
          {
            path: 'edit',
            element: <OrganizationEditPage />,
            loader: organizationEditLoader,
            handle: { titleKey: 'pages.admin.organizations.edit.title' },
          },
        ],
      },
      {
        path: '/workspaces/organizations/:id/move',
        element: <OrganizationMovePage />,
        loader: organizationMoveLoader,
        handle: { titleKey: 'pages.admin.organizations.move.title' },
      },
      {
        path: '/workspaces/properties',
        element: <PropertiesPage />,
        loader: propertiesLoader,
        shouldRevalidate: revalidateOn(propertiesLoaderParams),
        handle: { titleKey: 'pages.admin.properties.title' },
      },
      {
        path: '/workspaces/deals/deleted',
        element: <DeletedDealsPage />,
        loader: deletedDealsLoader,
        shouldRevalidate: revalidateOn(deletedDealsLoaderParams),
        handle: { titleKey: 'pages.admin.deals.deleted.title' },
      },
      {
        path: '/workspaces/companies/deleted',
        element: <DeletedCompaniesPage />,
        loader: deletedCompaniesLoader,
        shouldRevalidate: revalidateOn(deletedCompaniesLoaderParams),
        handle: { titleKey: 'pages.admin.companies.deleted.title' },
      },
      {
        path: '/workspaces/properties/:id',
        element: <PropertyDetailPage />,
        loader: propertyDetailLoader,
        handle: { titleKey: 'pages.admin.properties.detail.title' },
      },
      {
        path: '/workspaces/api-keys',
        element: <ApiKeysPage />,
        loader: apiKeysLoader,
        shouldRevalidate: revalidateOn(apiKeysLoaderParams),
        handle: { titleKey: 'pages.admin.api-keys.title' },
        children: [
          {
            path: 'new',
            element: <IssueApiKeyPage />,
            handle: { titleKey: 'pages.admin.api-keys.issue.title' },
          },
        ],
      },
      {
        path: '/workspaces/api-keys/:id',
        element: <ApiKeyDetailPage />,
        loader: apiKeyDetailLoader,
        handle: { titleKey: 'pages.admin.api-keys.detail.title' },
      },
      {
        // The Logs section is a container, not a page: `/logs` resolves to the
        // first child this operator may read. It cannot be a static <Navigate>
        // because that target depends on the permission set (IMM-494.1).
        path: '/logs',
        loader: logsIndexLoader,
      },
      {
        path: '/logs/api-calls',
        element: <ApiCallsPage />,
        loader: apiCallsLoader,
        shouldRevalidate: revalidateOn(apiCallsLoaderParams),
        handle: { titleKey: 'pages.admin.api-calls.title' },
      },
      {
        path: '/logs/api-calls/:callId',
        element: <ApiCallDetailPage />,
        loader: apiCallDetailLoader,
        handle: { titleKey: 'pages.admin.api-calls.detail.title' },
      },
      {
        path: '/logs/api-key-requests',
        element: <ApiKeyRequestsPage />,
        loader: apiKeyRequestsLoader,
        shouldRevalidate: revalidateOn(apiKeyRequestsLoaderParams),
        handle: { titleKey: 'pages.admin.api-key-requests.title' },
      },
      {
        // One canonical address per entry, with no key segment — an entry whose
        // key never resolved could never be addressed under one (IMM-494.2).
        path: '/logs/api-key-requests/:requestId',
        element: <ApiKeyRequestDetailPage />,
        loader: apiKeyRequestDetailLoader,
        handle: { titleKey: 'pages.admin.api-key-requests.detail.title' },
      },
      {
        path: '/logs/emails',
        element: <EmailLogPage />,
        loader: emailLogLoader,
        handle: { titleKey: 'pages.admin.email-log.title' },
        shouldRevalidate: shouldRevalidateEmailLog,
      },
      // The old `/workspaces/api-calls*` and `/workspaces/api-keys/:id/requests/*`
      // addresses are deliberately NOT kept alive (IMM-494.1 `## Decisions`) — no
      // backwards compatibility is wanted for an operator-only surface that ships
      // with a notice calling itself temporary. The detail address is genuinely
      // unmatched and hits the `*` not-found; the LIST address `/workspaces/api-calls`
      // matches `/workspaces/:id` above, so it is refused by that loader's uuid
      // guard and rendered as not-found by the error boundary (IMM-513). Both land
      // on the same page by two different paths.
      {
        path: '/valuations',
        element: <ValuationRequestsPage />,
        loader: valuationRequestsLoader,
        shouldRevalidate: revalidateOn(valuationRequestsLoaderParams),
        handle: { titleKey: 'pages.admin.valuations.title' },
      },
      {
        path: '/valuations/:id',
        element: <ValuationRequestDetailPage />,
        loader: valuationRequestDetailLoader,
        handle: { titleKey: 'pages.admin.valuations.title' },
      },
      {
        path: '/messenger/chats',
        element: <MessengerChatsPage />,
        loader: messengerChatsLoader,
        shouldRevalidate: revalidateOn(messengerChatsLoaderParams),
        handle: { titleKey: 'pages.admin.messenger-chats.title' },
      },
      {
        path: '/messenger/chats/:chatId',
        element: <MessengerChatDetailPage />,
        loader: messengerChatDetailLoader,
        handle: { titleKey: 'pages.admin.messenger-chats.title' },
      },
      // The chats list was WhatsApp-only before IMM-1127.1; bookmarks keep working.
      { path: '/whatsapp/chats', loader: () => redirect('/messenger/chats') },
      {
        path: '/whatsapp/chats/:chatId',
        loader: ({ params }: LoaderFunctionArgs) => redirect(`/messenger/chats/${params.chatId ?? ''}`),
      },
      {
        path: '/admin/skills/document-analyses',
        element: <DocumentAnalysisSkillsPage />,
        loader: documentAnalysisSkillsLoader,
        shouldRevalidate: revalidateOn(documentAnalysisSkillsLoaderParams),
        handle: { titleKey: 'pages.admin.skills.document-analyses.title' },
      },
      {
        path: '/admin/skills/document-analyses/:category',
        element: <DocumentAnalysisSkillEditorPage />,
        loader: documentAnalysisSkillEditorLoader,
        handle: { titleKey: 'pages.admin.skills.document-analyses.title' },
      },
      {
        // The system fallback is a sibling resource, not a `:category` value —
        // its own route so it is never captured by the category param above.
        path: '/admin/skills/system/document-analysis-fallback',
        element: <SystemSkillEditorPage />,
        loader: systemSkillEditorLoader,
        handle: { titleKey: 'pages.admin.skills.document-analyses.title' },
      },
      {
        // `new` is its own sibling route, ahead of `:key`, so the create page is
        // never captured as a definition whose key happens to be "new".
        path: '/admin/skills/agent-skills',
        element: <AgentSkillsPage />,
        loader: agentSkillsLoader,
        shouldRevalidate: revalidateOn(agentSkillsLoaderParams),
        handle: { titleKey: 'pages.admin.skills.agent-skills.title' },
      },
      {
        path: '/admin/skills/agent-skills/new',
        element: <NewAgentSkillPage />,
        handle: { titleKey: 'pages.admin.skills.agent-skills.editor.create-title' },
      },
      {
        path: '/admin/skills/agent-skills/:key',
        element: <AgentSkillEditorPage />,
        loader: agentSkillEditorLoader,
        handle: { titleKey: 'pages.admin.skills.agent-skills.title' },
      },
      {
        path: '/admin/skills/fact-keys',
        element: <FactKeysPage />,
        loader: factKeysLoader,
        shouldRevalidate: revalidateOn(factKeysLoaderParams),
        handle: { titleKey: 'pages.admin.skills.fact-keys.title' },
      },
      {
        path: '/admin',
        loader: () => redirect('/admins'),
      },
      // Operators moved off the redundant `/admin/` prefix (IMM-587.6) — the whole
      // app is the admin app. No redirect from the old `/admin/admins(/:id)`: this
      // is an internal operator console, not a published surface. That reverses
      // IMM-587.6's original "redirect the old paths" decision; the reversal was
      // ratified in IMM-606, which amended the PRD AC, the slice AC and its
      // decision log to match. The absence is deliberate and pinned by
      // `__tests__/operator-key-routes.test.tsx` — do not "fix" it by adding
      // redirects without reopening that decision.
      {
        path: '/admins',
        element: <AdminsPage />,
        loader: adminsLoader,
        shouldRevalidate: revalidateOn(adminsLoaderParams),
        handle: { titleKey: 'pages.admin.admins.title' },
        children: [
          {
            path: 'new',
            element: <NewAdminPage />,
            handle: { titleKey: 'pages.admin.admins.create.title' },
          },
          {
            // Issuing takes no operator argument, and the path says so: an admin
            // API key binds to the ACTING operator and the request body carries no
            // adminId, so a `:adminId` here would be a lie the URL tells.
            path: 'api-keys/new',
            element: <IssueAdminKeyPage />,
            handle: { titleKey: 'pages.admin.api-keys.issue-service.title' },
          },
        ],
      },
      {
        path: '/admins/:adminId/api-keys',
        element: <AdminApiKeysPage />,
        loader: adminApiKeysLoader,
        handle: { titleKey: 'pages.admin.admins.api-keys.title' },
      },
      {
        path: '/admins/:adminId/api-keys/:id',
        element: <ApiKeyDetailPage />,
        loader: apiKeyDetailLoader,
        handle: { titleKey: 'pages.admin.api-keys.detail.title' },
      },
      {
        path: '/admins/:id',
        element: <AdminDetailPage />,
        loader: adminDetailLoader,
        handle: { titleKey: 'pages.admin.admins.detail.title' },
      },
      {
        // MCP-Zugang (IMM-522.1). The mint dialog is a nested child so it
        // renders over the list, matching the API-keys surface; there is no
        // detail route — the list IS the surface.
        path: '/admin/mcp-access',
        element: <McpAccessPage />,
        loader: mcpAccessLoader,
        shouldRevalidate: revalidateOn(mcpAccessLoaderParams),
        handle: { titleKey: 'pages.admin.mcp-access.title' },
        children: [
          {
            path: 'new',
            element: <IssueMcpAccessPage />,
            handle: { titleKey: 'pages.admin.mcp-access.issue.title' },
          },
        ],
      },
      {
        // The valuation agent's token (IMM-1402). Not a list page, so no `shouldRevalidate`: the dialogs
        // revalidate it when they finish. A 403 from the loader lands on access-denied.
        path: '/valuation-agent',
        element: <ValuationAgentPage />,
        loader: valuationAgentLoader,
        handle: { titleKey: 'pages.admin.valuation-agent.title' },
      },
      {
        // Fleet — status and enrolment approvals (IMM-910.7). One route, one
        // call: the nav's group path and its single child both resolve here.
        path: '/fleet',
        element: <FleetPage />,
        loader: fleetLoader,
        handle: { titleKey: 'pages.admin.fleet.title' },
      },
      {
        // The jobs page is gone: the board shows every job, and its ticket panel answers questions and releases a
        // held job. Links in messages already sent still land somewhere useful.
        path: '/fleet/jobs',
        element: <Navigate to="/fleet/board" replace />,
      },
      {
        // The tracker snapshot as a kanban (fleet-agent ADR 0011, step 2). No loader: the page reads in a component
        // poll, like the LandIQ board, so the skeleton shows at once and a re-read keeps the cards.
        path: '/fleet/board',
        element: <FleetBoardPage />,
        handle: { titleKey: 'pages.admin.fleet-board.title' },
      },
      {
        // The fleet's token usage (IMM-911 Metriken). Read from the run ledger
        // on the backend; view, period, agents and class live in the URL so a
        // shared link shows the same picture.
        path: '/fleet/metrics',
        element: <FleetMetricsPage />,
        loader: fleetMetricsLoader,
        handle: { titleKey: 'pages.admin.fleet-metrics.title' },
      },
      {
        // One machine's health over a chosen period (IMM-1450). The overview feeds the machine select; nothing in
        // it depends on the search, so a filter change does not re-read it.
        path: '/fleet/history',
        element: <FleetHistoryPage />,
        loader: fleetLoader,
        shouldRevalidate: revalidateOn([]),
        handle: { titleKey: 'pages.admin.fleet-history.title' },
      },
      {
        // The accounts the workers hold (fleet-agent ADR 0027), with their settings. A fast read of our own
        // backend, so a loader.
        path: '/fleet/accounts',
        element: <FleetAccountsPage />,
        loader: fleetAccountsLoader,
        handle: { titleKey: 'pages.admin.fleet-accounts.title' },
      },
      {
        // LandIQ's data-runs board (IMM-1137). No loader: the page reads in a component poll so the skeleton
        // shows at once and a refresh keeps the cards; a 403 navigates to /access-denied.
        path: '/landiq/data-runs',
        element: <LandIqDataRunsPage />,
        handle: { titleKey: 'pages.admin.landiq-data-runs.title' },
      },
      {
        // One data-run entry on its own shareable page (IMM-1132.3). No loader: a click navigates at once and the
        // page reads LandIQ behind a skeleton; an unknown id renders the not-found page, LandIQ's failures the shell's.
        path: '/landiq/data-runs/:entryId',
        element: <LandIqDataRunDetailPage />,
        handle: { titleKey: 'pages.admin.landiq-data-run.breadcrumb' },
      },
      {
        // The sandbox's latest data reset (IMM-1476). No loader: GitHub is a slow remote read, so the page shows a
        // skeleton at once; on the hosted sandbox the page renders not-found itself.
        path: '/sandbox',
        element: <SandboxPage />,
        handle: { titleKey: 'pages.admin.sandbox.title' },
      },
    ],
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
];

// Sentry's data-router wrapper: it hooks into the router so pageload and
// navigation transactions are named by matched route pattern. Pairs with
// reactRouterBrowserTracingIntegration in sentry-init.ts. A no-op when Sentry
// is not initialised (no DSN).
const sentryCreateBrowserRouter = wrapCreateBrowserRouter(createBrowserRouter);

export const router = sentryCreateBrowserRouter(routes);
